import clsx from 'clsx'
import { useEffect, useRef, useState } from 'react'
import { FooterButtons, Icon, SpoilerTip, SsoLogin, TextField, Title, Text } from 'ui'
import { showCustomNotification } from '~/helperFunctions/customNotifications'
import { useActionErrorNotification } from '~/hooks/useActionErrorNotification'
import { ELIGIBILITY_TOAST } from '~/routes/eligibility'
import classes from '~/styles/eligibility/eligibility.module.scss'
import { clientValidation } from '~/utils/clientValidation'
import { clientDeleteCookie, logGenericPosthogEvent } from '~/utils/commonUtils'
import {
  ELIGIBILITY_FORM,
  ELIGIBILITY_HEADING_H1,
  ELIGIBILITY_HEADING_YELLOW_H1,
  ELIGIBILITY_SUBHEADING,
  ELIGIBILITY_TIP,
  ELIGIBILITY_WAITLIST_MESSAGE,
  ELIGIBILITY_WAITLIST_MESSAGE_YELLOW,
  MAXIMUM_RETRY_SSO,
  SSO_LOGIN_LIST,
} from '~/utils/constant'
import { DynamicProps, Props } from '~/utils/eligiblityType'
import { handleOnEnterKeyPress } from '~/utils/handleOnEnterKeyPress'
import { handleStartApplicationStorageData } from '~/utils/handleStartApplicationStorageData'
import { printError } from '~/utils/printError'
import { regex } from '~/utils/regex'
import { scrollToError } from '~/utils/scrollToError'
import { setDataLayer } from '~/utils/setDataLayer'
import { setObjectCookie } from '~/utils/setObjectCookie'

import { Container, Flex, Grid, Space } from '@mantine/core'
import { useLocalStorage, useMediaQuery } from '@mantine/hooks'
import { Form, useActionData, useLoaderData, useNavigate, useSubmit } from '@remix-run/react'

import { handleFormValidations } from '../common/handleClientValidation'
import { ROUTES } from '../common/routes'
import { LoaderData } from '../common/types'
import { schema } from './schema'
import { ActionData } from './schema'

export default function Index() {
  const navigate = useNavigate()
  const actionData = useActionData<ActionData>()
  useActionErrorNotification(actionData)
  const loaderData = useLoaderData() as LoaderData
  const [data, setData] = useState<ActionData | undefined>(actionData)
  const formRef = useRef<HTMLFormElement | undefined>(null)
  const submit = useSubmit()
  const [isSsoFlowInitiated, SetIsSsoFlowInitiated] = useState(false)
  const [actionInProgress, setActionInProgress] = useState(false)
  const [ssoLoginError, setSsoLoginError] = useState('')
  const isMobile = useMediaQuery('(max-width: 767px)')

  const [, , removeExistUserLocalStorage] = useLocalStorage({
    key: 'existUserLocalStorage',
    defaultValue: {},
  })
  const [, , removeAYBackButtonValue] = useLocalStorage({
    key: 'AYBackButtonValue',
    defaultValue: {},
  })

  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        document.addEventListener('keydown', handleOnEnterKeyPress)
        handleStartApplicationStorageData(removeFunctions)
      }
    } catch (error) {
      printError(error, 'USEEFFECTLOCAL')
    }
    return () => {
      document.removeEventListener('keydown', handleOnEnterKeyPress)
    }
  }, [])
  const removeFunctions = {
    AYBackButtonValue: removeAYBackButtonValue,
    userExists: removeExistUserLocalStorage,
  }

  useEffect(() => {
    try {
      setObjectCookie('currentUserEmail', null)
      if (document) {
        clientDeleteCookie(['loginPassChange', 'existingUser'])
      }
      const dataLayerObj = (window as any).dataLayer || []
      setDataLayer('user_id')
    } catch (err) {
      // Handle Error
    }
  }, [])

  useEffect(() => {
    if (actionData) {
      setActionInProgress(false)
      if (!actionData?.status) {
        const message = actionData.formError || actionData.error
        const withFieldError =
          message && !actionData.errors?.fieldErrors
            ? {
              ...actionData,
              errors: { fieldErrors: { emailID: [message] } },
            }
            : actionData
        scrollToError(withFieldError as ActionData)
        setData(withFieldError as ActionData)
        return
      }
      navigate(ROUTES.PERSONA)
    }
  }, [actionData, navigate])

  const dynamicProps = {
    preferedname: {
      defaultValue: loaderData?.preferedname,
      error: data?.errors,
      keyPress: regex.text,
    },
    firstName: {
      defaultValue: loaderData?.firstName,
      error: data?.errors,
      keyPress: regex.text,
    },
    lastName: {
      defaultValue: loaderData?.lastName,
      error: data?.errors,
      keyPress: regex.text,
    },
    emailID: {
      defaultValue: loaderData?.emailID,
      readOnly: loaderData?.newApp === 'true',
      error: data?.errors,
    },
  }
  const injectTextField = (updatedFieldInfo, item: number) => {
    return (
      <TextField
        {...updatedFieldInfo}
        key={item}
        id={updatedFieldInfo?.id}
        placeholder={updatedFieldInfo?.placeholder}
        label={updatedFieldInfo?.label}
        defaultValue={updatedFieldInfo?.defaultValue}
        readOnly={updatedFieldInfo?.readOnly}
        type={updatedFieldInfo?.type}
        onPaste={updatedFieldInfo?.onPaste}
        error={updatedFieldInfo?.error}
        updatedValue={updatedFieldInfo?.updatedValue}
        rightSection={updatedFieldInfo?.rightSection}
        className={classes.textFeild}
        variant="tertiaryBold"
        style={{ margin: '0px !important' }}
        deleteable={true}
        maxLength={updatedFieldInfo?.maxLength}
        onlyTextAllowed={updatedFieldInfo?.onlyTextAllowed}
      />
    )
  }
  const horizontalFormJsx = (
    field: Props['constant']['ELIGIBILITY_FORM']['child'][number],
    item: number,
    commonProps: any,
    dynamicProps: DynamicProps,
  ) => {
    return (
      <Grid key={item} classNames={{ root: classes.horizontalContainer }}>
        {field?.grandChild?.map((grandField, index) => {
          const { id = '' } = grandField || {}
          const updatedFieldInfo = { ...grandField, ...dynamicProps?.[id], ...commonProps }
          return (
            <Grid.Col key={id} span={{ base: 12, md: 12, lg: 6 }}>
              {injectTextField(updatedFieldInfo, item)}
              <Space h={10} />
            </Grid.Col>
          )
        })}
      </Grid>
    )
  }

  const verticalFormJsx = (
    field: Props['constant']['ELIGIBILITY_FORM']['child'][number],
    item: number,
    commonProps: any,
    dynamicProps: DynamicProps,
  ) => {
    const { id = '', placeholder = '', label = '' } = field || {}
    const updatedFieldInfo = { ...field, ...dynamicProps?.[id], ...commonProps }
    return (
      <Grid key={item}>
        <Grid.Col span={{ base: 12, md: 12, lg: 12 }}>
          {injectTextField(updatedFieldInfo, item)}
          <Space h={10} />
        </Grid.Col>
      </Grid>
    )
  }

  const fieldConfigs = ELIGIBILITY_FORM?.child?.map((field, item) => {
    const commonProps = {
      type: 'text',
      onPaste: (e: any) => e.preventDefault(),
    }
    if (field?.isAlignHorizontal) {
      return horizontalFormJsx(field, item, commonProps, dynamicProps)
    } else {
      return verticalFormJsx(field, item, commonProps, dynamicProps)
    }
  })

  const handleFormSubmit = () => {
    try {
      const response = handleFormValidations(formRef, schema)
      logGenericPosthogEvent('eligibility', response)
      if (response.errors) {
        scrollToError(response as ActionData)
        setData(response as ActionData)
      } else {
        submit(response.formData!, { method: 'post' })
      }
    } catch (err) {
      setActionInProgress(false)
      const message = err instanceof Error && err.message ? err.message : ELIGIBILITY_TOAST.MESSAGE
      showCustomNotification({
        message,
        title: ELIGIBILITY_TOAST.TITLE,
        color: 'red',
      })
    }
  }
  const initiateSsoLogin = async (e: Event) => {
    logGenericPosthogEvent('SSO Initiated', 'eligibility')
    SetIsSsoFlowInitiated(true)
    try {
      const redirectSsoURL = await fetch(`/api/initiateSsoLogin`)
      const formattedResponse = await redirectSsoURL?.json()
      if (formattedResponse?.detail) {
        setSsoLoginError(MAXIMUM_RETRY_SSO)
        SetIsSsoFlowInitiated(false)
        return
      }
      window.location.href = formattedResponse?.response?.authorization_url
      SetIsSsoFlowInitiated(false)
    } catch (err) {
      SetIsSsoFlowInitiated(false)
      return null
    }
  }
  return (
    <>
      <Container size="lg" className="mainContainer containerWithNoBackButton">
        <Form
          method="post"
          onChange={clientValidation(schema, data, setData)}
          autoComplete="off"
          ref={formRef}
        >
          <Grid>
            <Grid.Col span={{ base: 12, md: 12, lg: 4, sm: 12 }}>
              <Flex
                classNames={{ root: classes.headingWrapper }}
                className={clsx(classes.hideInMobile)}
              >
                <Title order={3} variant="blue" fontWeight="700" className={classes.textStyle}>
                  {ELIGIBILITY_WAITLIST_MESSAGE}{' '}
                  <Title order={3} variant="yellow" fontWeight="700" className="displayInline">
                    {ELIGIBILITY_WAITLIST_MESSAGE_YELLOW}
                  </Title>
                </Title>
                <Text color={'#243D74'}>
                    We have reached our current funding capacity and are temporarily unable to offer new loans for 2026.
                  </Text>
              </Flex>
              {isMobile && (
                <Flex classNames={{ root: classes.headingWrapper }}>
                  <Title order={4} variant="blue" fontWeight="700" className="displayInline">
                    {ELIGIBILITY_WAITLIST_MESSAGE}{' '}
                    <Title order={4} variant="yellow" fontWeight="700" className="displayInline">
                      {ELIGIBILITY_WAITLIST_MESSAGE_YELLOW}
                    </Title>
                  </Title>
                  {/* <Title order={4} variant="yellow" fontWeight="700" className="displayInline">
                    {ELIGIBILITY_SUBHEADING}
                  </Title> */}
                  <Text color={'#243D74'}>
                    We have reached our current funding capacity and are temporarily unable to offer new loans for 2026.
                  </Text>
                </Flex>
              )}
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 12, lg: 8, sm: 12 }}>
              <Title
                order={3}
                variant="blue"
                fontWeight="400"
                className={clsx(classes.formtextStyle, classes.hideInMobile)}
              >
                <Icon name="graduation" />
                <span className={classes.formHeadingText}>
                  {ELIGIBILITY_FORM?.heading}
                  <b>{ELIGIBILITY_FORM?.highlighter}</b>
                </span>
              </Title>
              <Space h={10} />
              {!loaderData?.isSessionRunning && (
                <SsoLogin
                  ssoLoginData={SSO_LOGIN_LIST}
                  onClickAction={initiateSsoLogin}
                  isSsoFlowInitiated={isSsoFlowInitiated}
                  loginButtonText="Continue with"
                  deviderText="Or"
                  ssoErrorMsg={ssoLoginError}
                />
              )}
              <div className={classes?.fromFieldWarpper}>{fieldConfigs}</div>

              <Space h={20} />
              <FooterButtons
                backButtonId="back"
                id="next"
                showOutlineButton={false}
                showFilledButton={true}
                uppercase={true}
                filledButtonText={'next'}
                customVariant="secondary"
                loading={actionInProgress}
                handleClientSide
                filledButtonOnClick={handleFormSubmit}
                pageData={data}
              />
              <Grid className={classes.hideInMobile}>
                <Grid.Col>
                  <div>
                    <SpoilerTip id="tip" className={classes.tip}>
                      <span dangerouslySetInnerHTML={{ __html: ELIGIBILITY_TIP }} />
                    </SpoilerTip>
                  </div>
                </Grid.Col>
              </Grid>
            </Grid.Col>
          </Grid>
        </Form>
      </Container>
      <div className="svgContainer">
        <Icon name="welcomeWave" />
      </div>
    </>
  )
}
